import { useAppStore } from '@/app/stores/app.store.js';
import { useNotificationsStore } from '@/app/stores/notifications.store.js';
import { useDomainsStore } from '#modules/domains/stores/domains.store.js';
import { useSupportStore } from '#modules/support/stores/support.store.js';
import { useSupportMessageStore } from '#modules/support/stores/support-message.store.js';
import { useTicketStore } from '#modules/support/stores/ticket.store.js';
import { useSSLStore } from '#modules/ssl/stores/ssl.store.js';
import { useTeamStore } from '#modules/team/stores/team.store.js';
import { usePermissionsStore } from '#modules/auth/stores/permissions.store.js';
import { useNotificationPreferencesStore } from '#modules/team/stores/notification-preferences.store.js';
import { setLanguage } from '@/app/lang/set-language.js';
import Bugsnag from '@bugsnag/js';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';
import { nextTick } from 'vue';

export function useStores(
    stores = [
        useAuthStore,
        useNotificationsStore,
        useDomainsStore,
        useSSLStore,
        useTeamStore,
        useSupportStore,
        useSupportMessageStore,
        usePermissionsStore,
        useTicketStore,
        useNotificationPreferencesStore,
    ]
) {
    return stores.map((useStore) => useStore());
}

export async function hydrate() {
    const stores = useStores();

    const appStore = useAppStore();
    const authStore = useAuthStore();
    const teamStore = useTeamStore();
    const permissionsStore = usePermissionsStore();

    if (appStore.hydrated || appStore.hydrating) {
        return;
    }

    Bugsnag.leaveBreadcrumb('Hydration started', {
        type: 'state',
        metadata: {
            authenticated: authStore.isAuthenticated,
            hasUser: !!authStore.user,
        },
    });

    appStore.hydrating = true;

    try {
        /**
         * Multiple stores rely on the authStore, teamStore, and permissionsStore to be set,
         * so they can fetch user specific data. The following makes sure that these stores
         * are always fetched first, before we hydrate anything else.
         */
        await authStore.hydrate();
        await permissionsStore.hydrate();
        await teamStore.hydrate();

        if (
            authStore.user &&
            authStore.isVerified &&
            teamStore.hasRequiredInformation &&
            teamStore.isVerified &&
            teamStore.isUnlocked &&
            (teamStore.enforces2FA ? authStore.has2FA : true)
        ) {
            const hydratedStores = ['auth', 'permissions', 'team'];
            await Promise.all(
                stores.filter(({ $id }) => !hydratedStores.includes($id)).map((store) => store.hydrate?.())
            );

            Bugsnag.leaveBreadcrumb('All stores hydrated', { type: 'state' });
        }

        await setLanguage();

        // Hydration process completed
        appStore.hydrated = true;
    } catch (error) {
        Bugsnag.leaveBreadcrumb('Hydration error', {
            type: 'error',
            metadata: {
                error: error.message,
            },
        });
        appStore.setError(error);
        throw error;
    } finally {
        await nextTick(() => {
            appStore.hydrating = false;
        });
    }

    Bugsnag.leaveBreadcrumb('Hydration completed', {
        type: 'state',
        metadata: {
            hasUser: !!authStore.user,
            hydrated: true,
        },
    });
}

export async function dehydrate(stores = useStores()) {
    const appStore = useAppStore();

    appStore.setError(null);

    if (appStore.hydrated === false) {
        return;
    }

    for (const store of stores) {
        await store.dehydrate?.();
    }

    appStore.hydrated = false;
}
